/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionRequest,
  ActionSuccess,
  AssetUtils,
  Ducks,
  RelativeTime,
  Seconds,
  TvAssetUtils,
} from '@wit/vtv-x-foundation-lib';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { StoreState } from 'store/store';
import VisualScrubberUtils from 'services/utils/VisualScrubberUtils';
import Constants from 'configs/Constants';
import * as StreamWatchers from './streamWatchers';
import { Sprite } from './data';

export enum ActionType {
  INIT = 'app/visualScrubber/sprite/INIT',
  SUCCESS = 'app/visualScrubber/sprite/SUCCESS',
  REQUEST_SPRITE = 'app/visualScrubber/sprite/REQUEST_SPRITE',
}

interface RequestSpriteMetadata {
  progress: Seconds;
  liveProgress: Seconds;
  indexes: Indexes;
}

interface Indexes {
  spriteIndex: number;
  thumbnailIndex: number;
}

type Init = ActionRequest<ActionType.INIT, number>;
type Success = ActionSuccess<
  ActionType.SUCCESS,
  {
    url: string;
    spriteIndex: number;
  }
>;
type RequestSprite = ActionRequest<ActionType.REQUEST_SPRITE, RequestSpriteMetadata>;

export type Actions = Init | Success | RequestSprite;

const Actions = {
  init: (spriteIndex: number): Init => ({
    type: ActionType.INIT,
    meta: spriteIndex,
  }),
  success: (url: string, spriteIndex: number): Success => ({
    type: ActionType.SUCCESS,
    payload: { url, spriteIndex },
  }),
  requestSprite: (progress: Seconds, liveProgress: Seconds, indexes: Indexes): RequestSprite => ({
    type: ActionType.REQUEST_SPRITE,
    meta: { progress, liveProgress, indexes },
  }),
};

export const ActionCreators = {
  requestSprite: Actions.requestSprite,
};

export const Selectors = {
  getSprites: (state: StoreState) => state.visualScrubber.sprites,
  getSpriteUrl: (state: StoreState, index: number) => state.visualScrubber.sprites[index]?.data,
  isSpriteProcessed: (state: StoreState, index: number) => state.visualScrubber.sprites[index]?.processed === true,
  getError: (state: StoreState, index: number) => state.visualScrubber.sprites[index]?.error,
};

function* initSprite(progress: Seconds, spriteIndex: number): SagaIterator {
  const activeStream: StreamWatchers.ActiveStream | undefined = yield select(
    StreamWatchers.Selectors.getActiveStreamData
  );
  if (!activeStream || progress < 0) {
    return;
  }

  yield put(Actions.init(spriteIndex));

  const { asset, url } = activeStream;

  const queryParams = VisualScrubberUtils.getUrlParameters(asset, progress, spriteIndex);

  yield put(Actions.success(queryParams ? url + queryParams : '', spriteIndex));
}

function* initPrevAndNextSprites(progress: Seconds, mainSpriteIndex: number): SagaIterator {
  const sprites: Sprite[] = yield select(Selectors.getSprites);

  const isPrevSpriteProcessed: boolean = yield select(Selectors.isSpriteProcessed, mainSpriteIndex - 1);
  if (mainSpriteIndex > 1 && !isPrevSpriteProcessed) {
    yield call(initSprite, progress, mainSpriteIndex - 1);
  }
  const isNextSpriteProcessed: boolean = yield select(Selectors.isSpriteProcessed, mainSpriteIndex + 1);
  if (mainSpriteIndex + 1 < sprites?.length && !isNextSpriteProcessed) {
    yield call(initSprite, progress, mainSpriteIndex + 1);
  }
}

function* initSpritesInLiveMode({ progress, liveProgress, indexes }: RequestSpriteMetadata): SagaIterator {
  const { spriteIndex } = indexes;

  const isSpriteProcessed: boolean = yield select(Selectors.isSpriteProcessed, spriteIndex);

  const remainingProgressToLive = liveProgress - progress;
  const isSpriteLive = Constants.SECONDS_PER_SPRITE >= remainingProgressToLive;

  if (isSpriteLive && liveProgress % VisualScrubberUtils.getThumbnailInterval() === 0) {
    const liveProgressIndexes = VisualScrubberUtils.getPositionsFromProgress(liveProgress);

    const liveThumbnailThreshold =
      VisualScrubberUtils.getSpriteThreshold(liveProgressIndexes.spriteIndex, true) +
      VisualScrubberUtils.getProgressAdjustmentForLivePosition(liveProgressIndexes.thumbnailIndex);

    yield call(initSprite, liveThumbnailThreshold, liveProgressIndexes.spriteIndex);
    return;
  }

  if (!isSpriteProcessed) {
    yield call(initSprite, VisualScrubberUtils.getSpriteThreshold(spriteIndex), spriteIndex);
  }
}

function* requestSprite(action: RequestSprite): SagaIterator {
  const isVisualScrubberOn = yield select(Ducks.Dms.Selectors.isVisualScrubberOn);
  if (!isVisualScrubberOn) {
    return;
  }

  const { progress, indexes } = action.meta;
  const { spriteIndex } = indexes;

  const activeStream: StreamWatchers.ActiveStream | undefined = yield select(
    StreamWatchers.Selectors.getActiveStreamData
  );

  if (progress < 0 || spriteIndex < 0 || !activeStream) {
    return;
  }

  const isLive =
    AssetUtils.isEpg(activeStream.asset) && TvAssetUtils.getRelativeTime(activeStream.asset) === RelativeTime.LIVE;

  if (isLive) {
    yield call(initSpritesInLiveMode, action.meta);
    return;
  }

  const isSpriteProcessed: boolean = yield select(Selectors.isSpriteProcessed, spriteIndex);

  if (!isSpriteProcessed) {
    yield call(initSprite, progress, spriteIndex);
  }

  yield call(initPrevAndNextSprites, progress, spriteIndex);
}

export function* sagas() {
  yield all([takeEvery(ActionType.REQUEST_SPRITE, requestSprite)]);
}
